// Free / 100-credit / 3-day trial — single source of truth for the offer's
// numbers, feature flag, and holdout assignment (PANDA-1500).
//
// PRICING CHANGE (2026-09-02): the opening charge is now $0. The offer was
// "$1 today, then $29/month from day 3"; it is now "card today, nothing
// charged, then $29/month from day 3". The recurring side is UNCHANGED —
// TRIAL_CONVERT_CENTS is still derived from the real Pro rung, so no catalogue
// price moved and no existing subscriber is affected.
//
// WHY THE OPENING CHARGE WENT AWAY. PayPal is the live processor and its
// advanced-vaulting (reference transactions) entitlement is still pending. A
// PayPal plan can carry a $0 TRIAL cycle, and approving that subscription
// still collects and authorises a funding source — verified against a live
// sandbox subscription: "Pay with Visa x-11", consent worded as pre-approved
// payments, subscription ACTIVE, last_payment null, next_billing_time exactly
// TRIAL_DAYS out. So the card is on file from minute one without any charge
// and without the entitlement.
//
// WHAT THIS COSTS. The $1 was doing quiet card-verification work: a capture
// proves the instrument is live. At $0 nothing is proven until the day-3
// conversion, so a higher share of conversions will fail. That makes the
// dunning path load-bearing rather than nice-to-have — see the note on
// user_billing.payment_state in lib/billing/payment-issue.ts.
//
// Pricing decision (2026-07-14): the trial converts to TODAY's real Pro —
// $29/mo, 300 credits (lib/pricing/credit-tiers.ts, post-PANDA-1414) — NOT the
// ticket/design mock's legacy "$22 / 650 credits", which is the grandfathered
// pre-cutover price 650 is no longer a valid Pro rung. All trial copy derives
// its figures from here, so the offer can never contradict the real catalog.

import { tierMonthlyCents, PLAN_BASE_CREDITS } from "@/lib/pricing/credit-tiers";

// ── Trial mechanics ───────────────────────────────────────────────────────
// $0 captured today. The card is still COLLECTED at checkout — every processor
// authorises a funding source to open a subscription — it is simply not
// charged until the trial converts. Zero is a legal value everywhere it is
// used; the one thing it must never do is reach a fulfiller that identifies a
// payment BY this amount, because no such payment now arrives. See
// lib/payments/paypal/trial.ts for how the opening is detected instead.
export const TRIAL_PRICE_CENTS = 0;
export const TRIAL_CREDITS = 100; // build credits, scoped to the trial

/**
 * What the trial used to cost, for code that reads trials STARTED BEFORE
 * 2026-09-02.
 *
 * Every trial opened under the old offer captured exactly $1, and the paths
 * that repair or re-fulfil one identify it by that amount. When
 * TRIAL_PRICE_CENTS became 0 those comparisons silently stopped matching — the
 * constant kept its name and changed its meaning, so a legacy trial became
 * unrepairable without a single line of code changing or a test failing.
 *
 * Kept separate rather than folded into a range, because the two are different
 * facts: one is the price of today's offer, the other is a historical amount
 * that will never change again. It is also the only price the Stripe, Paddle
 * and Chargebee trial fulfillers ever accept — those processors cannot open a
 * free trial (see the guard in app/api/billing/trial/start/route.ts), so a $1
 * capture is the only trial payment they can legitimately receive.
 */
export const LEGACY_TRIAL_PRICE_CENTS = 100;

export const TRIAL_DAYS = 3;

/**
 * Lowest fraction of TRIAL_CONVERT_CENTS that still counts as "the trial
 * converted to Pro" when a conversion is detected by invoice amount.
 *
 * Exists because "any paid invoice above the $1 trial price" stops working the
 * moment anything adds tax to the $1 — a Merchant of Record makes the trial
 * invoice 108-125c, which clears a $1 threshold and is then misread as a
 * conversion: trial remainder forfeited, trial_status latched to 'converted'
 * (terminal), and 300 Pro credits granted for a $1 charge.
 *
 * 0.4 sits comfortably below the discounted-Pro floor (the cancel-flow coupon
 * is 50% off, i.e. 0.5) and far above any taxed $1 (about 0.043 of $29).
 */
export const TRIAL_CONVERT_MIN_FRACTION = 0.4;

// ── What the trial converts to (real Pro today) ───────────────────────────
export const TRIAL_CONVERT_PLAN = "pro" as const;
export const TRIAL_CONVERT_CREDITS = PLAN_BASE_CREDITS.pro; // 300
export const TRIAL_CONVERT_CENTS = tierMonthlyCents(
  "pro",
  TRIAL_CONVERT_CREDITS,
); // $29.00 (2900)

// ── Post-trial Pro price (Stripe subscription-schedule phase 2) ────────────
// The $1 trial is a Stripe subscription SCHEDULE: phase 1 is a short $1 /
// TRIAL_DAYS-day price (charged at checkout, ONE clean invoice line), phase 2 is
// this recurring Pro price ($29/mo) that Stripe bills automatically when the
// trial ends. Schedule phases can only reference a pre-created Stripe Price/
// Product id (inline product_data is NOT allowed for schedule phases), and the
// app otherwise prices Pro dynamically — so this ONE price must exist in Stripe
// and be wired here. Create a $29.00/month USD recurring Price on a "Pro"
// product in Stripe (test + live) and set STRIPE_PRICE_TRIAL_PRO in each env.
// Its amount MUST equal TRIAL_CONVERT_CENTS; the 300 credits are granted by
// Panda on conversion, independent of the Stripe price.
export function getTrialProPriceId(): string | null {
  return process.env.STRIPE_PRICE_TRIAL_PRO || null;
}

// ── The Stripe schedule, and why it is now unreachable ───────────────────
// Stripe ran this trial as a subscription SCHEDULE specifically to AVOID a free
// trial: native trial_period_days forced Stripe's non-customisable "Free trial
// ends {date}" label and a confusing $0 invoice line, so phase 1 was made a
// short $1 price instead. That reasoning was sound for a $1 offer and does not
// survive a $0 one — there is no longer a charge for phase 1 to carry.
//
// Stripe is NOT re-mechanised here. It is not the live processor
// (NEXT_PUBLIC_PAYMENT_PROVIDER selects one, and it is paypal), so its branch in
// app/api/billing/trial/start/route.ts cannot be reached — and rewriting a
// blocked processor's trial against a schedule nobody can exercise would ship
// untested money code. That branch instead REFUSES while the opening price is
// zero, so flipping the flag can never charge $1 against copy that says free.
// Re-mechanising Stripe is the work that unblocks that flag, and it is
// deliberately not done yet.

// Telemetry marker — spread onto every trial analytics event (server
// billing_trial_* + client trial_* funnel) so the offer's shape is
// self-describing downstream rather than inferred from an echoed amount.
//
// Kept as a marker rather than deleted now that the trial is free: the funnel
// still needs to tell THIS trial apart from the $1 one it replaced, and every
// historical event carries is_paid_trial: true. Flipping the values keeps that
// comparison possible in one breakdown; removing the properties would silently
// merge two different offers into one series.
export const TRIAL_MARKER = {
  is_paid_trial: false,
  trial_type: "free_3_day",
  trial_price_cents: TRIAL_PRICE_CENTS,
} as const;

/**
 * @deprecated Renamed to TRIAL_MARKER when the opening charge went to $0 — the
 * old name asserts something no longer true. Kept as an alias only so a
 * straggling import cannot silently drop the marker from an event.
 */
export const PAID_TRIAL_MARKER = TRIAL_MARKER;

// ── Offer lifecycle ───────────────────────────────────────────────────────
// The offer is available for 7 days after first exposure, then the user reverts
// to standard pricing. Makes "last call" honest and gives T4 a real deadline.
export const OFFER_EXPIRY_DAYS = 7;

// T2 (full-price-dismiss fallback): first dismiss, then at most once more after
// a 7-day cooldown → max 2 exposures per 30 days, only while free + active.
export const T2_MAX_PER_30D = 2;
export const T2_COOLDOWN_DAYS = 7;

// T3 (exit-intent on the $1 ad-landing): once per visitor, ~30-day cookie.
export const T3_COOKIE_DAYS = 30;
export const T3_COOKIE = "panda_trial_exit_seen";

// Cookie planted by the T1 ad-landing so the offer survives the OAuth signup
// round-trip (localStorage / linkUtmToUser do not — see the attribution map).
export const OFFER_COOKIE = "panda_trial_offer";
export const OFFER_COOKIE_DAYS = 7;

// ── Exposure surfaces (trial_offer_exposures.surface + analytics `trigger`) ─
export type TrialSurface =
  | "t1_signup"
  | "t2_dismiss"
  | "t3_exit"
  | "t4_email"
  // T5 — in-build credit gate (PANDA-1613 follow-up). Its own surface so it is
  // exempt from t2_dismiss's anti-farming cap: a free, trial-eligible user who
  // runs out of credits mid-build should always be offered the $1 trial.
  | "t5_credit_gate"
  // T6 — any in-app "Upgrade" entry point (header button, mobile sheet, credit
  // chip, settings, feature gates…). The product rule is that an eligible free
  // user meets the $1 offer at EVERY upgrade checkpoint, not only after running
  // out of credits, so this surface behaves like T5: no deadline, no T2 cap.
  | "t6_upgrade_entry"
  // T7 — immediately after a user's first successful build.
  | "t7_first_build"
  // T8 — the upgrade CTA shown after a positive product survey.
  | "t8_survey";

/**
 * Every legal surface, as an object the compiler ties to the union.
 *
 * The two API routes used to keep their own hand-written `TrialSurface[]`
 * arrays and silently coerce anything unrecognised to "t1_signup". Adding a
 * member to the union above therefore compiled cleanly while the routes kept
 * rewriting the new surface's name — which both mis-attributed the exposure and
 * (because t1_signup is not expiry-exempt) could 403 a user who had just been
 * shown the modal. `satisfies` makes that omission a build error instead.
 */
export const TRIAL_SURFACES = {
  t1_signup: true,
  t2_dismiss: true,
  t3_exit: true,
  t4_email: true,
  t5_credit_gate: true,
  t6_upgrade_entry: true,
  t7_first_build: true,
  t8_survey: true,
} as const satisfies Record<TrialSurface, true>;

/** Runtime narrowing for untrusted input (request bodies, query strings). */
export function isTrialSurface(value: unknown): value is TrialSurface {
  return typeof value === "string" && value in TRIAL_SURFACES;
}

// ── Survey sources behind t8_survey ───────────────────────────────────────
//
// `t8_survey` is ONE surface covering four genuinely different moments, and
// until now the only thing telling them apart downstream was the free-form
// `entrypoint` string on the upgrade family — which the trial funnel
// (trial_offer_shown/_clicked/_dismissed) and the money events carry in a
// different vocabulary, so "which survey sold this $1 trial?" could not be
// asked in one breakdown. This is that key: a small, closed set, safe to break
// funnels down by, emitted as `survey_id` alongside the answer that preceded
// the offer.
export type TrialSurveySource =
  /** "Did Panda build what you expected?" — components/workspace/build-quality-survey.tsx */
  | "build_quality"
  /** Return-to-chat satisfaction survey — components/chat/return-feedback-survey.tsx */
  | "return_feedback"
  /** Pre-publish exit-intent survey's thank-you step — components/workspace/exit-intent-survey.tsx */
  | "exit_intent"
  /** Thumbs-up on a chat message — the lightest-weight positive response. */
  | "chat_thumbs_up";

/** Every legal survey source, tied to the union by the compiler — same guard
 *  TRIAL_SURFACES gives TrialSurface, for the same reason: a new member must
 *  not be able to ship without every reader knowing about it. */
export const TRIAL_SURVEY_SOURCES = {
  build_quality: true,
  return_feedback: true,
  exit_intent: true,
  chat_thumbs_up: true,
} as const satisfies Record<TrialSurveySource, true>;

/**
 * Surfaces exempt from the OFFER_EXPIRY_DAYS deadline.
 *
 * Deliberately an ALLOWLIST of the in-app surfaces rather than a deny-list of
 * the "campaign" ones. A deny-list looks equivalent and is not: the /start
 * landing page reads eligibility with a hard-coded "t1_signup"
 * (app/start/start-client.tsx) for BOTH the ad-click and the exit-intent path,
 * and the T4 email's landing click arrives there too — so "keep the deadline
 * for t3_exit/t4_email" would have governed zero real requests while silently
 * exempting the one surface those users actually hit.
 *
 * What the deadline still governs: t1_signup, t2_dismiss, t3_exit, t4_email —
 * i.e. every surface reachable from the ad landing or the backstop email, which
 * is exactly where "Last call — your $1 trial expires soon"
 * (lib/email/trial-offer.ts) is claimed. Those stay honest.
 */
export const EXPIRY_EXEMPT_SURFACES: ReadonlySet<TrialSurface> = new Set([
  "t5_credit_gate",
  "t6_upgrade_entry",
  "t7_first_build",
  "t8_survey",
]);

/**
 * Surfaces the app raises on its OWN initiative, with no user action.
 *
 * The single source of truth for "did the user ask for this?", used by both the
 * person-level exposure ceiling below and the dismissal latch in
 * components/trial/trial-dismiss-fallback.tsx.
 */
export const AUTO_DISPATCH_SURFACES: ReadonlySet<TrialSurface> = new Set([
  "t7_first_build",
]);

/**
 * Ceiling on UNSOLICITED exposures, per person per 7 days.
 *
 * Deliberately does NOT apply to surfaces the user clicked.
 *
 * The first version capped every surface, and that was wrong in a way that only
 * shows up in production: a genuinely eligible free user who had met the offer
 * 12 times — trivially reached once it appears at every upgrade entry point —
 * started being handed the FULL-PRICE plan picker instead. They had done
 * nothing wrong and nothing had changed about their account; they had simply
 * pressed Upgrade often enough to exhaust a counter meant for nagging.
 *
 * A click is not nagging. It is the user asking, and the answer must not depend
 * on how many times they have asked before. There is also nothing to farm: the
 * trial is one-per-account and one-per-card, enforced server-side at
 * /api/billing/trial/start, so an unbounded number of IMPRESSIONS costs
 * nothing. Only the modal that appears on its own needs a bound.
 */
export const PERSON_MAX_EXPOSURES_PER_7D = 12;

// ── Feature flag (client + server read the same NEXT_PUBLIC_ switch) ──────
export function isTrialOfferEnabled(): boolean {
  return process.env.NEXT_PUBLIC_TRIAL_OFFER_ENABLED === "1";
}

// ── Card-level trial guards: REMOVED (2026-08-24) ────────────────────────
// There were two — refuse a PREPAID card, and refuse a card that had already
// taken a trial on another account — each behind its own env flag, and neither
// flag was ever set in any environment.
//
// Both are gone rather than left dormant, because neither could run before the
// money moved: `card.funding` and `card.fingerprint` only exist once the card
// does, which is AFTER the $1 is captured. So every refusal was necessarily a
// charge-then-refund, costing the non-returned processing fee on top of a lost
// sale — and both misfired on ordinary customers. Prepaid fired three times in
// two days on people whose everyday bank card is issued as prepaid (Chime, Cash
// App, Current); card-reuse fired on a household sharing one debit card, over a
// row our own fulfilment race had left behind.
//
// THE LIMIT THAT REMAINS is the one the business actually wants: ONE TRIAL PER
// ACCOUNT, enforced by user_billing.trial_used in lib/trial/eligibility.ts —
// checked BEFORE checkout, where no money has moved and a refusal is free.
//
// Fingerprints and card funding are still RECORDED (trial_cards,
// user_credits.card_funding) so the pattern stays measurable. If blocking is
// ever wanted again, prefer a Stripe Radar rule
// (`Block if :card_funding: = 'prepaid'`): Radar declines BEFORE capture, so
// there is no charge, no refund and no fee.

// ── Holdout ───────────────────────────────────────────────────────────────
// No PostHog flag/experiment infra exists in-repo, so the holdout is a
// deterministic server-side assignment stamped as an event/person property
// (`trial_cohort`). Holdout users are NEVER shown the offer — they stay on the
// normal funnel so we can measure cannibalisation. Default 10% holdout; a
// stable hash of the user id keeps assignment consistent across every read.
// Defaults to 0 — OFF. The product rule is absolute: every free user who has
// never paid and never used the trial sees the $1 offer at every upgrade
// checkpoint. A holdout is a measurement device, and a measurement device must
// never be the thing that silently withholds the offer from a tenth of the
// audience because an environment variable was not set. Opt IN to it by setting
// NEXT_PUBLIC_TRIAL_HOLDOUT_PERCENT, and only for as long as the experiment runs.
// Read at CALL time, not module-eval time: this is an experiment knob, and
// freezing it at import made it impossible to exercise the holdout logic in a
// test without module-registry surgery — which is why the default silently
// mattered so much.
export function holdoutPercent(): number {
  const raw = Number(process.env.NEXT_PUBLIC_TRIAL_HOLDOUT_PERCENT ?? "0");
  return Number.isFinite(raw) ? Math.max(0, Math.min(100, raw)) : 0;
}

export type TrialCohort = "offer" | "holdout";

// FNV-1a — small, stable, dependency-free. Same input → same bucket forever.
function stableHashPercent(key: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0) % 100;
}

export function trialCohort(userId: string): TrialCohort {
  const pct = holdoutPercent();
  return stableHashPercent(`trial:${userId}`) < pct ? "holdout" : "offer";
}

// ── Display helpers (never hardcode these figures in components/emails) ────
export const trialCopy = {
  /** What the buyer pays TODAY, as a label. "Free", not "$0" — $0 reads as a
   *  broken price where "Free" reads as the offer. */
  priceLabel: "Free",
  credits: TRIAL_CREDITS,
  days: TRIAL_DAYS,
  convertPriceLabel: `$${Math.round(TRIAL_CONVERT_CENTS / 100)}`, // "$29"
  convertCredits: TRIAL_CONVERT_CREDITS, // 300
  // "then $29/mo from day 3 · cancel anytime"
  renewalLine: `then $${Math.round(TRIAL_CONVERT_CENTS / 100)}/mo from day ${TRIAL_DAYS} · cancel anytime`,
  /**
   * Machine-readable terms line required on every T4 email (DMCC/FTC).
   *
   * A FREE trial that auto-converts is a negative-option offer, and the
   * disclosure bar is higher than it was at $1: a customer who pays nothing
   * today has no charge to remind them a subscription started, so the line must
   * state the card, the amount, WHEN it is taken, and how to stop it. "Free
   * trial, cancel anytime" alone is the formulation regulators single out.
   */
  termsLine: `Free for ${TRIAL_DAYS} days — card required, then $${Math.round(
    TRIAL_CONVERT_CENTS / 100,
  )}/mo automatically on day ${TRIAL_DAYS} unless you cancel first`,
  /** Short form for tight surfaces (buttons, chips). Still names the charge. */
  shortTermsLine: `Free ${TRIAL_DAYS} days, then $${Math.round(
    TRIAL_CONVERT_CENTS / 100,
  )}/mo`,
} as const;
